import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Polyline, Marker, CircleMarker, Tooltip, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Truck as TruckIcon, Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { apiBaseUrl, authHeaders } from "./shared";
import { TILE_LAYER_ATTRIBUTION, useTileLayerUrl } from "../../mapTiles";

// Per-truck route-history map with playback. Unions the three lifecycle position
// sources the backend returns (loaded / deadhead / idle), narrates the trip as it
// plays, and surfaces live utilization. See routers/truck_history.py.

const C = {
  loaded: "#2f9e5e", deadhead: "#d4820a", idle: "#8a97a6",
  pickup: "#2f9e5e", delivery: "#2563eb", fuel: "#7c3aed", fuelp: "#7c3aed", toll: "#b07d14", offroute: "#dc3b4b",
  good: "#2f9e5e", warn: "#d4820a", bad: "#dc3b4b", ink: "#0f172a",
};
const DAY = 864e5;

// ---- date helpers ----------------------------------------------------------
const startOfWeek = (dt) => { const x = new Date(dt); const wd = (x.getDay() + 6) % 7; x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - wd); return x; };
const toYMD = (dt) => { const x = new Date(dt); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const fmtMD = (dt) => new Date(dt).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const fmtDT = (ts) => new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const fmtT = (ts) => new Date(ts).toLocaleString("en-US", { hour: "numeric", minute: "2-digit" });
const ms = (iso) => (iso ? new Date(iso).getTime() : null);
const money0 = (v) => (v == null || isNaN(v) ? "—" : `$${Math.round(v).toLocaleString()}`);
const money2 = (v) => (v == null || isNaN(v) ? "—" : `$${Number(v).toFixed(2)}`);

function truckIcon() {
  return L.divIcon({ html: `<div style="width:26px;height:26px;border-radius:50%;background:#0f172a;border:2px solid #fff;display:flex;align-items:center;justify-content:center;font-size:13px;box-shadow:0 0 6px rgba(0,0,0,.5)">🚚</div>`, className: "", iconSize: [26, 26], iconAnchor: [13, 13] });
}
function pinIcon(color, glyph) {
  const html = `<div style="position:relative;width:24px;height:32px"><svg width="24" height="32" viewBox="0 0 24 32"><path d="M12 0C5.4 0 0 5.4 0 12c0 8.5 12 20 12 20s12-11.5 12-20C24 5.4 18.6 0 12 0z" fill="${color}"/><circle cx="12" cy="12" r="8" fill="#fff"/></svg><div style="position:absolute;top:4px;left:0;width:24px;text-align:center;font-size:12px;font-weight:800;color:${color}">${glyph}</div></div>`;
  return L.divIcon({ html, className: "", iconSize: [24, 32], iconAnchor: [12, 32] });
}
const dotIcon = (color, size = 15) => L.divIcon({ html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 3px rgba(0,0,0,.4)"></div>`, className: "", iconSize: [size, size], iconAnchor: [size / 2, size / 2] });

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => { if (bounds && bounds.length) { try { map.fitBounds(L.latLngBounds(bounds), { padding: [50, 50], maxZoom: 9 }); } catch (e) { /* noop */ } } }, [bounds, map]);
  return null;
}

export default function TruckHistoryMap() {
  // Optional deep-link (e.g. from the Finance > Empty Miles tab): ?truck_id=<uuid>&start=YYYY-MM-DD&end=YYYY-MM-DD
  // seeds the truck + a custom date range. Absent -> unchanged default behaviour (first truck, this week).
  const [sp] = useSearchParams();
  const spTruck = sp.get("truck_id") || "";
  const spStart = sp.get("start");
  const spEnd = sp.get("end");
  const [trucks, setTrucks] = useState([]);
  const [truckId, setTruckId] = useState(spTruck);
  const [filter, setFilter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [show, setShow] = useState({ loaded: true, deadhead: true, idle: true, pickups: true, deliveries: true, fuel: true, fuelp: true, toll: true, stops: true, offroute: true });
  const [revealAll, setRevealAll] = useState(false); // pins appear as the scrubber passes, unless "All"
  // date range
  const [mode, setMode] = useState(spStart && spEnd ? "custom" : "week");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [cStart, setCStart] = useState(spStart || toYMD(new Date(Date.now() - 6 * DAY)));
  const [cEnd, setCEnd] = useState(spEnd || toYMD(new Date()));
  // playback
  const [scrub, setScrub] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const tileUrl = useTileLayerUrl();
  const reqRef = useRef(0);

  const range = useMemo(() => {
    if (mode === "week") return { start: toYMD(weekStart), end: toYMD(new Date(weekStart.getTime() + 6 * DAY)) };
    return { start: cStart, end: cEnd };
  }, [mode, weekStart, cStart, cEnd]);

  useEffect(() => {
    axios.get(`${apiBaseUrl}/fleet/truck-history/trucks`, authHeaders())
      .then((r) => setTrucks(r.data.trucks || [])).catch(() => setError("Couldn't load the truck list."));
  }, []);

  useEffect(() => {
    if (!truckId) { setData(null); return; }
    const my = ++reqRef.current;
    setLoading(true); setError(null); setPlaying(false);
    axios.get(`${apiBaseUrl}/fleet/truck-history`, { ...authHeaders(), params: { truck_id: truckId, start: `${range.start}T00:00:00`, end: `${range.end}T23:59:59` } })
      .then((r) => { if (my === reqRef.current) setData(r.data); })
      .catch((err) => { if (my === reqRef.current) setError(err.response?.data?.detail || "Couldn't load truck history."); })
      .finally(() => { if (my === reqRef.current) setLoading(false); });
  }, [truckId, range.start, range.end]);

  const filteredTrucks = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? trucks.filter((t) => (t.unit_number || "").toLowerCase().includes(q) || (t.driver_name || "").toLowerCase().includes(q)) : trucks;
  }, [trucks, filter]);

  // ---- normalize segments + build playback timeline ------------------------
  const timeline = useMemo(() => {
    if (!data) return { play: [], chapters: [], bounds: [] };
    const segs = [];
    (data.loaded_segments || []).forEach((s) => show.loaded && segs.push({ kind: "loaded", pts: s.points, miles: s.miles, t0: ms(s.from), t1: ms(s.to), origin: s.load?.origin, dest: s.load?.destination, appt: ms(s.load?.delivery_at), actualDel: ms(s.load?.actual_delivery_at) || ms(s.to), ext: s.load?.external_load_id }));
    (data.deadhead_segments || []).forEach((s) => show.deadhead && segs.push({ kind: "deadhead", pts: s.points, miles: s.miles, t0: ms(s.from), t1: ms(s.to), dest: s.load?.origin }));
    (data.idle_segments || []).forEach((s) => show.idle && segs.push({ kind: "idle", pts: s.points, miles: s.miles, t0: ms(s.from), t1: ms(s.to) }));
    segs.sort((a, b) => a.t0 - b.t0);
    const play = []; const cum = { loaded: 0, deadhead: 0, idle: 0 }; const chapters = [];
    segs.forEach((s) => {
      const m = s.pts.length; const startIdx = play.length;
      s.pts.forEach((p, i) => {
        if (i > 0 && m > 1) cum[s.kind] += s.miles / (m - 1);
        play.push({ lat: p[0], lng: p[1], kind: s.kind, t: s.t0 + (m > 1 ? i / (m - 1) : 0) * (s.t1 - s.t0), seg: s, prog: m > 1 ? i / (m - 1) : 1, cum: { ...cum }, isStart: i === 0, isEnd: i === m - 1 });
      });
      chapters.push({ s, startIdx, endIdx: play.length - 1 });
    });
    const bounds = [];
    segs.forEach((s) => s.pts.forEach((p) => bounds.push(p)));
    return { play, chapters, bounds };
  }, [data, show.loaded, show.deadhead, show.idle]);

  // reset scrub to end whenever the timeline changes
  useEffect(() => { setScrub(Math.max(0, timeline.play.length - 1)); setPlaying(false); }, [timeline]);

  // playback ticker
  useEffect(() => {
    if (!playing || timeline.play.length < 2) return;
    const step = Math.max(1, Math.floor(timeline.play.length / 150));
    const id = setInterval(() => {
      setScrub((s) => { const n = s + step; if (n >= timeline.play.length - 1) { setPlaying(false); return timeline.play.length - 1; } return n; });
    }, Math.max(45, 160 / speed));
    return () => clearInterval(id);
  }, [playing, speed, timeline]);

  const play = timeline.play;
  const atEnd = scrub >= play.length - 1;
  const head = play[scrub];
  // progressive reveal: an event marker shows once the scrubber passes its timestamp (or when at
  // end / "All" is on). Keeps a long range from drowning in P/D/fuel/toll pins. Timeless events
  // (e.g. unconfirmed pickups with no actual time) show only at the end.
  const evShown = (at) => revealAll || atEnd || !head || (at != null && ms(at) <= head.t);

  // traveled trail split into consecutive same-kind runs
  const trailRuns = useMemo(() => {
    if (!play.length || atEnd) return [];
    const runs = []; let cur = null;
    for (let i = 0; i <= scrub; i++) {
      const p = play[i];
      if (!cur || cur.kind !== p.kind) { cur = { kind: p.kind, pts: [[p.lat, p.lng]] }; runs.push(cur); }
      else cur.pts.push([p.lat, p.lng]);
      // bridge the join so runs connect visually
      if (cur.pts.length === 1 && i > 0) cur.pts.unshift([play[i - 1].lat, play[i - 1].lng]);
    }
    return runs;
  }, [play, scrub, atEnd]);

  const seekFrac = (frac) => {
    if (play.length < 2) return;
    const t = play[0].t + frac * (play[play.length - 1].t - play[0].t);
    let bi = 0, bd = Infinity;
    play.forEach((p, i) => { const dd = Math.abs(p.t - t); if (dd < bd) { bd = dd; bi = i; } });
    setPlaying(false); setScrub(bi);
  };

  const st = data?.stats;
  // ---- audit panel derived (Phase 2) ----
  const econ = data?.economics || {};
  // Cost = revenue − profit so it ALWAYS reconciles (profit is revenue minus ALL trip costs incl.
  // insurance/payroll; summing the partial cost_breakdown under-counted and broke the arithmetic).
  const costSum = (econ.revenue != null && econ.profit != null)
    ? econ.revenue - econ.profit
    : Object.values(econ.cost_breakdown || {}).reduce((a, b) => a + (b || 0), 0);
  const trips = data?.trips || [];
  const plannedFuel = data?.planned_fuel_stops || [];
  const fuelMatched = plannedFuel.filter((f) => f.status === "matched").length;
  const fuelMissed = plannedFuel.filter((f) => f.status === "missed").length;
  const ppm = (econ.profit != null && st?.loaded_miles) ? money2(econ.profit / st.loaded_miles) : "—";
  const tollPasses = data?.toll_passes || [];
  const CB = econ.cost_breakdown || {};
  const costRows = [
    ["Fuel", CB.fuel_cost, C.fuel], ["Driver", CB.driver_cost, "#6366f1"], ["Tolls", CB.toll_cost, C.toll],
    ["Maintenance", CB.maintenance_cost, C.idle], ["Insurance", CB.insurance_cost, "#8b5cf6"],
    ["Office payroll", CB.office_payroll_cost, "#a855f7"], ["Other", CB.other_cost, "#94a3b8"],
  ].filter((r) => r[1] != null && r[1] !== 0);
  const costMax = Math.max(1, ...costRows.map((r) => r[1] || 0));
  const lateN = trips.filter((t) => t.on_time === false).length;
  const unconfP = (data?.pickup_pins || []).filter((p) => !p.confirmed).length;
  const unconfD = (data?.delivery_pins || []).filter((p) => !p.confirmed).length;
  const util = (c) => { const sum = c.loaded + c.deadhead + c.idle; return sum > 0 ? Math.round(c.loaded / sum * 100) : 0; };
  const utilColor = (u) => (u >= 85 ? C.good : u >= 70 ? C.warn : C.bad);

  return (
    <div style={S.page}>
      <div>
        <h1 style={S.h1}><TruckIcon size={20} /> Truck History Map</h1>
        <p style={S.sub}>One truck's full movement — loaded, deadhead and idle. Navigate by week or custom range, toggle layers, and press play to replay the trip.</p>
      </div>

      <div style={S.controls}>
        <input style={S.search} placeholder="Filter by unit # or driver…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <select style={S.select} value={truckId} onChange={(e) => setTruckId(e.target.value)}>
          <option value="">Select a truck…</option>
          {filteredTrucks.map((t) => <option key={t.id} value={t.id}>#{t.unit_number}{t.driver_name ? ` — ${t.driver_name}` : ""}{t.status ? ` (${t.status})` : ""}</option>)}
        </select>
      </div>

      <div style={S.daterow}>
        <button style={S.btn} onClick={() => { setMode("week"); setWeekStart(startOfWeek(new Date())); }}>This week</button>
        <button style={S.btn} onClick={() => { setMode("week"); setWeekStart(startOfWeek(new Date(Date.now() - 7 * DAY))); }}>Last week</button>
        <span style={S.step}>
          <button style={S.stepBtn} onClick={() => { setMode("week"); setWeekStart(new Date(weekStart.getTime() - 7 * DAY)); }}>‹</button>
          <span style={S.stepLbl}>{fmtMD(range.start)} – {fmtMD(range.end)}</span>
          <button style={S.stepBtn} onClick={() => { setMode("week"); setWeekStart(new Date(weekStart.getTime() + 7 * DAY)); }}>›</button>
        </span>
        <button style={{ ...S.btn, ...(mode === "custom" ? S.btnOn : {}) }} onClick={() => setMode(mode === "custom" ? "week" : "custom")}>Custom range</button>
        {mode === "custom" && (
          <span style={S.custom}>
            <input type="date" style={S.date} value={cStart} onChange={(e) => setCStart(e.target.value)} />
            <span style={{ color: "var(--muted)" }}>→</span>
            <input type="date" style={S.date} value={cEnd} onChange={(e) => setCEnd(e.target.value)} />
          </span>
        )}
      </div>

      {data?.truck && <div style={S.truckLine}><strong>#{data.truck.unit_number}</strong>{data.truck.driver_name ? ` · ${data.truck.driver_name}` : ""}{data.truck.make ? ` · ${data.truck.make} ${data.truck.model || ""}` : ""}</div>}

      {/* contextual status card */}
      {data && <StatusCard head={head} atEnd={atEnd} stats={st} range={range} util={util} utilColor={utilColor} />}

      <div style={S.auditLayout}>
       <div style={S.auditLeft}>
      <div style={S.mapWrap}>
        {loading && <div style={S.msg}><Loader2 size={16} /> Loading…</div>}
        {error && <div style={{ ...S.msg, color: C.bad }}>{error}</div>}
        {!truckId && !loading && <div style={S.msg}>Pick a truck to see its routes.</div>}
        {truckId && !loading && data && !timeline.bounds.length && <div style={S.msg}>No movement recorded for this truck in {fmtMD(range.start)}–{fmtMD(range.end)}.</div>}

        <MapContainer center={[39.5, -98.35]} zoom={4} style={{ height: "100%", width: "100%" }}>
          {tileUrl && <TileLayer attribution={TILE_LAYER_ATTRIBUTION} url={tileUrl} />}
          {timeline.bounds.length > 0 && <FitBounds bounds={timeline.bounds} />}

          {/* base routes (dim while partially played) */}
          {show.idle && (data?.idle_segments || []).map((s, i) => <Polyline key={`i${i}`} positions={s.points} pathOptions={{ color: C.idle, weight: 2, opacity: atEnd ? 0.6 : 0.18, dashArray: "1 6" }} />)}
          {show.deadhead && (data?.deadhead_segments || []).map((s, i) => <Polyline key={`d${i}`} positions={s.points} pathOptions={{ color: C.deadhead, weight: 3, opacity: atEnd ? 0.8 : 0.18, dashArray: "8 6" }} />)}
          {show.loaded && (data?.loaded_segments || []).map((s, i) => (
            <Polyline key={`l${i}`} positions={s.points} pathOptions={{ color: C.loaded, weight: 4, opacity: atEnd ? 0.85 : 0.18 }}>
              <Tooltip sticky>{s.load ? `${s.load.origin || "?"} → ${s.load.destination || "?"}` : "Loaded"} · {s.miles} mi</Tooltip>
            </Polyline>
          ))}

          {/* played trail */}
          {trailRuns.map((r, i) => <Polyline key={`t${i}`} positions={r.pts} pathOptions={{ color: C[r.kind], weight: r.kind === "loaded" ? 5 : 4, opacity: 1 }} />)}
          {!atEnd && head && <Marker position={[head.lat, head.lng]} icon={truckIcon()} />}

          {show.offroute && (data?.off_route_points || []).map((p, i) => <CircleMarker key={`o${i}`} center={p} radius={3} pathOptions={{ color: C.offroute, fillColor: C.offroute, fillOpacity: 0.8, weight: 1 }} />)}
          {show.stops && (data?.idle_markers || []).map((m, i) => evShown(m.from) && <Marker key={`s${i}`} position={[m.lat, m.lng]} icon={dotIcon(C.idle, 14)}><Popup><strong>Stopped {Math.round(m.minutes / 60 * 10) / 10}h</strong><br />{fmtDT(m.from)} → {fmtDT(m.to)}</Popup></Marker>)}
          {show.pickups && (data?.pickup_pins || []).map((p, i) => evShown(p.at) && <Marker key={`p${i}`} position={[p.lat, p.lng]} icon={pinIcon(p.confirmed ? C.pickup : "#9aa7b4", "P")}><Popup><strong>Pickup</strong> — {p.label || "?"}<br />{p.confirmed ? `Confirmed ${fmtDT(p.at)}` : "⚠ Pickup not confirmed"}</Popup></Marker>)}
          {show.deliveries && (data?.delivery_pins || []).map((p, i) => evShown(p.at) && <Marker key={`dl${i}`} position={[p.lat, p.lng]} icon={pinIcon(p.confirmed ? C.delivery : "#9aa7b4", "D")}><Popup><strong>Delivery</strong> — {p.label || "?"}<br />{p.confirmed ? `Confirmed ${fmtDT(p.at)}` : "⚠ Not confirmed"}</Popup></Marker>)}
          {/* planned fuel stops (hollow ring) — matched=solid ring, missed=dashed red ring */}
          {show.fuelp && (data?.planned_fuel_stops || []).filter((f) => f.lat != null && f.lng != null).map((f, i) => (
            <CircleMarker key={`fp${i}`} center={[f.lat, f.lng]} radius={9}
              pathOptions={{ color: f.status === "missed" ? C.offroute : C.fuel, weight: 2.5, fill: false, dashArray: f.status === "missed" ? "3 3" : null }}>
              <Popup><strong>Planned fuel{f.name ? ` — ${f.name}` : ""}</strong><br />{f.status === "matched" ? "✓ followed" : "⚠ missed (fueled elsewhere / skipped)"}<br />{f.planned_ppg != null ? `plan $${f.planned_ppg}/gal` : ""}{f.actual_ppg != null ? ` · paid $${f.actual_ppg}/gal` : ""}</Popup>
            </CircleMarker>
          ))}
          {show.fuel && (data?.fuel_stops || []).map((f, i) => evShown(f.at) && <Marker key={`f${i}`} position={[f.lat, f.lng]} icon={dotIcon(C.fuel, 16)}><Popup><strong>Fuel{f.station ? ` — ${f.station}` : ""}</strong><br />{fmtDT(f.at)}<br />{f.gallons != null ? `${f.gallons} gal` : ""}{f.price_per_gallon != null ? ` @ $${f.price_per_gallon}/gal` : ""}{f.total_cost != null ? ` · $${f.total_cost.toLocaleString()}` : ""}</Popup></Marker>)}
          {/* toll passes (gold) */}
          {show.toll && (data?.toll_passes || []).filter((t) => t.lat != null && t.lng != null).map((t, i) => evShown(t.at) && (
            <CircleMarker key={`tp${i}`} center={[t.lat, t.lng]} radius={5}
              pathOptions={{ color: C.toll, fillColor: C.toll, fillOpacity: 0.85, weight: 1 }}>
              <Popup><strong>Toll{t.name ? ` — ${t.name}` : ""}</strong><br />{t.price != null ? `$${t.price}` : ""}{t.source ? ` · ${t.source}` : ""}<br />{fmtDT(t.at)}<br /><small style={{ color: "var(--muted)" }}>reference price, not an invoice</small></Popup>
            </CircleMarker>
          ))}
        </MapContainer>

        <div style={S.legend}>
          {LEGEND.map(([k, label, dash, icon]) => (
            <button key={k} style={{ ...S.leg, opacity: show[k] ? 1 : 0.4 }} onClick={() => setShow((s) => ({ ...s, [k]: !s[k] }))}>
              {icon ? <span style={{ ...S.dot, background: C[k === "offroute" ? "offroute" : k === "stops" ? "idle" : k] }} /> : <span style={{ display: "inline-block", width: 18, height: 0, borderTop: `3px ${dash ? "dashed" : "solid"} ${C[k]}` }} />}
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* play bar */}
      <div style={S.playbar}>
        <button style={S.playBtn} disabled={play.length < 2} onClick={() => { if (atEnd) setScrub(0); setPlaying((p) => !p); }}>{playing ? "❚❚" : "▶"}</button>
        <input type="range" style={S.range} min={0} max={Math.max(0, play.length - 1)} value={scrub} onChange={(e) => { setPlaying(false); setScrub(+e.target.value); }} />
        <button style={S.btn} onClick={() => setSpeed(speed === 1 ? 2 : speed === 2 ? 4 : 1)}>{speed}×</button>
        <button style={{ ...S.btn, ...(revealAll ? S.btnOn : {}) }} onClick={() => setRevealAll((v) => !v)} title="Reveal pins as the truck reaches them, or show all at once">{revealAll ? "Pins: all" : "Pins: as driven"}</button>
        <div style={S.clock}>{head ? <>{fmtDT(head.t)}<br /><small style={{ color: "var(--muted)" }}>{scrub + 1} / {play.length}</small></> : <small style={{ color: "var(--muted)" }}>play the trip</small>}</div>
      </div>

      {/* chapter timeline */}
      {play.length >= 2 && (
        <div style={S.chapters} onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); seekFrac(Math.min(1, Math.max(0, (e.clientX - r.left) / r.width))); }}>
          {timeline.chapters.map((c, i) => {
            const T0 = play[0].t, T1 = play[play.length - 1].t, span = T1 - T0 || 1;
            const w = (c.s.t1 - c.s.t0) / span * 100;
            return <div key={i} title={c.s.kind === "loaded" ? `${c.s.origin} → ${c.s.dest}` : c.s.kind === "deadhead" ? `Deadhead → ${c.s.dest}` : "Idle"} style={{ ...S.chap, width: `${w}%`, background: C[c.s.kind] }}>{w > 9 ? (c.s.kind === "loaded" ? (c.s.dest || "load") : c.s.kind === "deadhead" ? "→ pickup" : "idle") : ""}</div>;
          })}
          {head && <div style={{ ...S.playhead, left: `${(head.t - play[0].t) / ((play[play.length - 1].t - play[0].t) || 1) * 100}%` }} />}
        </div>
      )}
       </div>{/* /auditLeft */}

       {/* ===== AUDIT RAIL (summary beside the map) ===== */}
       {data && (trips.length > 0 || econ.revenue != null) && (
         <div style={S.auditRail}>
          <div style={S.auditBand}>
            <AStat l="Revenue" v={money0(econ.revenue)} />
            <AStat l="Cost" v={money0(costSum)} />
            <AStat l="Profit" v={money0(econ.profit)} sub={econ.margin_pct != null ? `${econ.margin_pct}% margin` : ""} color={(econ.profit || 0) < 0 ? C.bad : C.good} />
            <AStat l="Profit / mi" v={ppm} sub="loaded mi" />
            <AStat l="Fuel plan → used" v={`${fuelMatched}/${plannedFuel.length}`} sub="stops followed" />
            <AStat l="Tolls (actual)" v={money0(econ.toll_actual)} sub="reference-priced" />
          </div>

          <div style={S.flags}>
            <Flag label="Late deliveries" n={lateN} tone={lateN > 0 ? "bad" : "ok"} />
            <Flag label="Missed fuel stops" n={fuelMissed} tone={fuelMissed > 0 ? "warn" : "ok"} />
            <Flag label="Unconfirmed pickups" n={unconfP} tone={unconfP > 0 ? "warn" : "ok"} />
            <Flag label="Unconfirmed deliveries" n={unconfD} tone={unconfD > 0 ? "warn" : "ok"} />
          </div>

          {costRows.length > 0 && (<>
            <div style={S.auditTitle}>Where the money went</div>
            <div style={S.break}>
              {costRows.map(([l, v, color]) => (
                <div key={l} style={S.brow}>
                  <span>{l}</span>
                  <div style={S.track}><i style={{ display: "block", height: "100%", borderRadius: 4, width: `${(v / costMax) * 100}%`, background: color }} /></div>
                  <span style={S.rnum}>{money0(v)}</span>
                </div>
              ))}
            </div>
          </>)}
         </div>
       )}
      </div>{/* /auditLayout */}

      {/* ===== AUDIT DETAIL TABLES (below the fold) ===== */}
      {data && (trips.length > 0 || econ.revenue != null) && (
        <div style={S.audit}>
          <div style={S.auditTitle}>Trips in this window</div>
          <div style={S.tblWrap}>
            <table style={S.tbl}>
              <thead><tr>
                {["Lane", "Pickup", "Revenue", "Profit", "$/mi", "Loaded", "Deadhead", "Fuel", "Off-rt", "On-time"].map((h, i) => (
                  <th key={i} style={{ ...S.th, textAlign: i >= 2 && i <= 8 ? "right" : "left" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {trips.map((t, i) => (
                  <tr key={t.load_id || i}>
                    <td style={S.td}>{t.lane}</td>
                    <td style={S.td}>{t.pickup_at ? String(t.pickup_at).slice(0, 10) : "—"}</td>
                    <td style={S.tdR}>{money0(t.revenue)}</td>
                    <td style={{ ...S.tdR, color: (t.profit || 0) < 0 ? C.bad : C.good }}>{money0(t.profit)}</td>
                    <td style={S.tdR}>{t.profit_per_mile != null ? money2(t.profit_per_mile) : "—"}</td>
                    <td style={S.tdR}>{Math.round(t.loaded_miles || 0).toLocaleString()}</td>
                    <td style={S.tdR}>{Math.round(t.deadhead_miles || 0).toLocaleString()}</td>
                    <td style={S.tdR}>{t.fuel_planned ? `${t.fuel_matched}/${t.fuel_planned}` : "—"}</td>
                    <td style={S.tdR}>{t.off_route_miles != null ? Math.round(t.off_route_miles) : "—"}</td>
                    <td style={S.td}>{t.on_time == null ? "—" : <span style={{ ...S.chip, background: t.on_time ? "rgba(47,158,94,.14)" : "rgba(220,59,75,.14)", color: t.on_time ? C.good : C.bad }}>{t.on_time ? "on time" : "late*"}</span>}</td>
                  </tr>
                ))}
                {trips.length === 0 && <tr><td colSpan={10} style={{ ...S.td, textAlign: "center", color: "var(--muted)" }}>No completed loads in this window.</td></tr>}
              </tbody>
            </table>
          </div>

          <div style={S.twoCol}>
            {plannedFuel.length > 0 && (
              <div>
                <div style={S.auditTitle}>Fuel — planned vs actual</div>
                <div style={S.tblWrap}>
                  <table style={S.tbl}>
                    <thead><tr>
                      {["Planned station", "Plan $/gal", "Actual $/gal", "Status"].map((h, i) => (
                        <th key={i} style={{ ...S.th, textAlign: i === 1 || i === 2 ? "right" : "left" }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {plannedFuel.map((f, i) => (
                        <tr key={i}>
                          <td style={S.td}>{[f.name, f.state].filter(Boolean).join(", ") || "—"}</td>
                          <td style={S.tdR}>{f.planned_ppg != null ? money2(f.planned_ppg) : "—"}</td>
                          <td style={S.tdR}>{f.actual_ppg != null ? money2(f.actual_ppg) : "—"}</td>
                          <td style={S.td}>
                            <span style={{ ...S.chip, background: f.status === "matched" ? "rgba(47,158,94,.14)" : "rgba(220,59,75,.14)", color: f.status === "matched" ? C.good : C.bad }}>
                              {f.status === "matched" ? "fueled" : "missed"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tollPasses.length > 0 && (
              <div>
                <div style={S.auditTitle}>Tolls — detected passes</div>
                <div style={S.tblWrap}>
                  <table style={S.tbl}>
                    <thead><tr>
                      {["When", "Toll", "Price", "Source"].map((h, i) => (
                        <th key={i} style={{ ...S.th, textAlign: i === 2 ? "right" : "left" }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {tollPasses.map((p, i) => (
                        <tr key={i}>
                          <td style={S.td}>{p.at ? String(p.at).slice(0, 16).replace("T", " ") : "—"}</td>
                          <td style={S.td}>{p.name || "—"}</td>
                          <td style={{ ...S.tdR, color: (p.price || 0) >= 100 ? C.bad : "inherit", fontWeight: (p.price || 0) >= 100 ? 700 : 400 }}>{p.price != null ? money2(p.price) : "—"}</td>
                          <td style={S.td}>{p.source || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div style={S.auditNote}>Revenue = load rate; <b>Cost = revenue − profit</b>. Profit is <b>trip-level</b> (revenue − fuel, driver, tolls, insurance, payroll, maintenance) — <b>before</b> the truck's fixed weekly equipment cost. All <b>modeled</b> planner figures (fuel via fixed MPG, not Relay receipts). <b>$/mi</b> can spike on short loads. <b>On-time*</b> is advisory — appointment times are often gamed. Fuel “plan→used” = recommended stops actually fueled at. Tolls = detected passes, reference-priced.</div>
        </div>
      )}
    </div>
  );
}

const AStat = ({ l, v, sub, color }) => (
  <div style={S.aStat}>
    <span style={S.aStatL}>{l}</span>
    <span style={{ ...S.aStatV, ...(color ? { color } : {}) }}>{v}</span>
    {sub ? <span style={S.aStatS}>{sub}</span> : null}
  </div>
);

const FLAG_TONE = {
  ok: { bg: "rgba(34,160,90,.12)", fg: "#1e9e58", dot: "#22a05a" },
  warn: { bg: "rgba(217,145,20,.14)", fg: "#b7791f", dot: "#d99114" },
  bad: { bg: "rgba(220,59,75,.14)", fg: "#dc3b4b", dot: "#dc3b4b" },
};
const Flag = ({ label, n, tone }) => {
  const t = FLAG_TONE[tone] || FLAG_TONE.ok;
  return (
    <div style={{ ...S.flag, background: t.bg, color: t.fg }}>
      <span style={{ ...S.flagDot, background: t.dot }} />
      <span style={S.flagN}>{n}</span>
      <span style={S.flagL}>{label}</span>
    </div>
  );
};

function StatusCard({ head, atEnd, stats, range, util, utilColor }) {
  let borderColor = C.ink, body;
  if (atEnd || !head) {
    const c = stats ? { loaded: stats.loaded_miles, deadhead: stats.deadhead_miles, idle: stats.idle_miles } : { loaded: 0, deadhead: 0, idle: 0 };
    const sum = c.loaded + c.deadhead + c.idle, empty = sum > 0 ? Math.round((c.deadhead + c.idle) / sum * 100) : 0;
    body = (
      <>
        <div style={S.scPhase}>
          <div style={{ ...S.scBadge, color: "var(--muted)" }}>◼ TRIP SUMMARY · {fmtMD(range.start)}–{fmtMD(range.end)}</div>
          <div style={S.scTitle}>{stats?.loads_count ?? 0} load{stats?.loads_count === 1 ? "" : "s"} · {Math.round(sum).toLocaleString()} total mi</div>
          <div style={S.scDetail}><span style={{ ...S.pill, background: "rgba(220,59,75,.14)", color: C.bad }}>{empty}% empty miles</span> &nbsp;press ▶ or scrub to replay</div>
        </div>
        {kpiStrip(c, util, utilColor, true)}
      </>
    );
  } else {
    const s = head.seg; borderColor = C[s.kind];
    let badge, title, detail, evt = null;
    if (s.kind === "loaded") {
      badge = "LOADED"; title = `${s.origin || "?"} → ${s.dest || "?"}`;
      detail = `${Math.round(head.prog * s.miles).toLocaleString()} / ${Math.round(s.miles).toLocaleString()} mi (${Math.round(head.prog * 100)}%)`;
      if (head.isEnd) { const late = s.appt && s.t1 > s.appt; evt = <span style={{ fontWeight: 700, color: late ? C.bad : C.good }}>🏁 Delivered {s.dest} · {fmtT(s.t1)}{s.appt ? ` · ${late ? "late" : "on time"}` : ""}</span>; }
      else if (head.isStart && scrubNotFirst(head)) evt = <span style={{ fontWeight: 700, color: C.good }}>📦 Picked up {s.origin}</span>;
    } else if (s.kind === "deadhead") {
      badge = "DEADHEAD · EMPTY"; title = `→ ${s.dest || "pickup"}`;
      detail = `${Math.round(head.prog * s.miles).toLocaleString()} / ${Math.round(s.miles).toLocaleString()} mi to pickup`;
    } else {
      badge = "IDLE · NO LOAD"; title = "Parked / idling";
      const hrs = (head.t - s.t0) / 36e5;
      detail = `sitting ${hrs < 1 ? Math.round(hrs * 60) + "m" : hrs.toFixed(1) + "h"} · ${Math.round(head.prog * s.miles)} idle mi`;
    }
    body = (
      <>
        <div style={S.scPhase}>
          <div style={{ ...S.scBadge, color: C[s.kind] }}><span style={{ ...S.scDot, background: C[s.kind] }} />{badge}</div>
          <div style={S.scTitle}>{title}</div>
          <div style={S.scDetail}>{detail}</div>
          {evt && <div>{evt}</div>}
        </div>
        {kpiStrip(head.cum, util, utilColor, false)}
      </>
    );
  }
  return <div style={{ ...S.statusCard, borderLeftColor: borderColor }}>{body}</div>;
}
function scrubNotFirst() { return true; }

function kpiStrip(c, util, utilColor, total) {
  const u = util(c);
  return (
    <div style={S.scKpis}>
      <Kpi v={Math.round(c.loaded).toLocaleString()} l="loaded mi" color={C.loaded} />
      <Kpi v={Math.round(c.deadhead).toLocaleString()} l="deadhead mi" color={C.deadhead} />
      <Kpi v={Math.round(c.idle).toLocaleString()} l="idle mi" color={C.idle} />
      <div style={S.kpi}><span style={{ fontSize: 20, fontWeight: 900, color: utilColor(u) }}>{u}%</span><span style={S.kpiL}>utilization{total ? "" : " so far"}</span></div>
    </div>
  );
}
const Kpi = ({ v, l, color }) => <div style={S.kpi}><span style={{ fontSize: 16, fontWeight: 800, color }}>{v}</span><span style={S.kpiL}>{l}</span></div>;

const LEGEND = [["loaded", "Loaded", false, false], ["deadhead", "Deadhead", true, false], ["idle", "Idle", true, false], ["pickups", "Pickups", false, true], ["deliveries", "Deliveries", false, true], ["fuel", "Fuel (actual)", false, true], ["fuelp", "Fuel (planned)", false, true], ["toll", "Tolls", false, true], ["stops", "Stops", false, true], ["offroute", "Off-route", false, true]];

const S = {
  page: { display: "flex", flexDirection: "column", minHeight: "100%", padding: 16, gap: 10, boxSizing: "border-box" },
  h1: { display: "flex", alignItems: "center", gap: 8, fontSize: 20, fontWeight: 800, margin: 0 },
  sub: { color: "var(--muted,#64748b)", fontSize: 13, margin: "4px 0 0" },
  controls: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  search: { padding: "8px 11px", borderRadius: 9, border: "1px solid var(--line,#e5e7eb)", fontSize: 13, minWidth: 200, background: "var(--card,#fff)", color: "var(--ink,#0f172a)" },
  select: { padding: "8px 11px", borderRadius: 9, border: "1px solid var(--line,#e5e7eb)", fontSize: 13, minWidth: 290, background: "var(--card,#fff)", color: "var(--ink,#0f172a)" },
  daterow: { display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" },
  btn: { padding: "7px 11px", borderRadius: 8, border: "1px solid var(--line,#e5e7eb)", background: "var(--card,#fff)", color: "var(--ink,#0f172a)", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnOn: { background: "#0f172a", color: "#fff", borderColor: "#0f172a" },
  step: { display: "inline-flex", alignItems: "center", border: "1px solid var(--line,#e5e7eb)", borderRadius: 8, overflow: "hidden" },
  stepBtn: { border: "none", background: "var(--card,#fff)", color: "var(--ink,#0f172a)", padding: "7px 10px", cursor: "pointer", fontSize: 14, fontWeight: 800 },
  stepLbl: { padding: "7px 12px", fontSize: 12.5, fontWeight: 700, minWidth: 140, textAlign: "center", borderLeft: "1px solid var(--line,#e5e7eb)", borderRight: "1px solid var(--line,#e5e7eb)" },
  custom: { display: "inline-flex", gap: 6, alignItems: "center" },
  date: { padding: "6px 8px", borderRadius: 8, border: "1px solid var(--line,#e5e7eb)", fontSize: 12.5, background: "var(--card,#fff)", color: "var(--ink,#0f172a)" },
  truckLine: { fontSize: 13.5 },
  statusCard: { display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", background: "var(--card,#fff)", border: "1px solid var(--line,#e5e7eb)", borderLeft: "5px solid #8a97a6", borderRadius: 12, padding: "11px 16px", minHeight: 62 },
  scPhase: { display: "flex", flexDirection: "column", gap: 2, minWidth: 230 },
  scBadge: { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 11, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase" },
  scDot: { width: 10, height: 10, borderRadius: "50%" },
  scTitle: { fontSize: 16, fontWeight: 800, letterSpacing: "-.01em" },
  scDetail: { fontSize: 12.5, color: "var(--muted,#64748b)" },
  scKpis: { display: "flex", gap: 18, marginLeft: "auto", flexWrap: "wrap" },
  kpi: { display: "flex", flexDirection: "column", alignItems: "flex-end" },
  kpiL: { fontSize: 10.5, color: "var(--muted,#64748b)", textTransform: "uppercase", letterSpacing: ".05em" },
  pill: { display: "inline-block", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 800 },
  mapWrap: { position: "relative", height: "50vh", minHeight: 320, borderRadius: 12, overflow: "hidden", border: "1px solid var(--line,#e5e7eb)" },
  auditLayout: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-start" },
  auditLeft: { flex: "2 1 460px", minWidth: 0, display: "flex", flexDirection: "column", gap: 10 },
  auditRail: { flex: "1 1 300px", minWidth: 0, display: "flex", flexDirection: "column", gap: 10, border: "1px solid var(--line,#e5e7eb)", borderRadius: 12, background: "var(--card,#fff)", padding: 12 },
  msg: { position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)", zIndex: 500, background: "rgba(255,255,255,0.95)", color: "#0f172a", padding: "8px 14px", borderRadius: 20, fontSize: 13, fontWeight: 600, boxShadow: "0 2px 10px rgba(0,0,0,0.15)", display: "flex", alignItems: "center", gap: 8 },
  legend: { position: "absolute", bottom: 12, left: 12, zIndex: 500, background: "rgba(255,255,255,0.96)", borderRadius: 10, padding: "8px 10px", display: "flex", flexWrap: "wrap", gap: 6, maxWidth: 360, boxShadow: "0 2px 10px rgba(0,0,0,0.15)" },
  leg: { display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#0f172a", padding: "2px 4px" },
  dot: { width: 12, height: 12, borderRadius: "50%", border: "2px solid #fff" },
  playbar: { display: "flex", alignItems: "center", gap: 12 },
  playBtn: { width: 40, height: 40, borderRadius: "50%", border: "none", background: "#0f172a", color: "#fff", cursor: "pointer", fontSize: 15, flexShrink: 0 },
  range: { flex: 1, cursor: "pointer" },
  clock: { minWidth: 132, textAlign: "right", fontSize: 12.5, fontWeight: 700 },
  chapters: { position: "relative", display: "flex", height: 30, borderRadius: 8, overflow: "hidden", border: "1px solid var(--line,#e5e7eb)", cursor: "pointer" },
  chap: { height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", borderRight: "1px solid rgba(255,255,255,.35)" },
  playhead: { position: "absolute", top: -3, bottom: -3, width: 2, background: "#0f172a", boxShadow: "0 0 0 1px #fff", pointerEvents: "none" },
  // ---- audit panel (Phase 2) ----
  audit: { display: "flex", flexDirection: "column", gap: 10, border: "1px solid var(--line,#e5e7eb)", borderRadius: 12, background: "var(--card,#fff)", padding: 14 },
  auditBand: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8 },
  aStat: { display: "flex", flexDirection: "column", gap: 2, background: "var(--card-2,#f6f8fb)", border: "1px solid var(--line,#e5e7eb)", borderRadius: 10, padding: "10px 12px" },
  aStatL: { fontSize: 10.5, color: "var(--muted,#64748b)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".03em" },
  aStatV: { fontSize: 20, fontWeight: 800, letterSpacing: "-.02em", fontVariantNumeric: "tabular-nums" },
  aStatS: { fontSize: 11, color: "var(--muted,#64748b)" },
  auditTitle: { fontSize: 11, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted,#64748b)", fontWeight: 700, marginTop: 4 },
  tblWrap: { overflowX: "auto", border: "1px solid var(--line,#e5e7eb)", borderRadius: 10 },
  tbl: { borderCollapse: "collapse", width: "100%", fontSize: 12.5 },
  th: { fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".03em", color: "var(--muted,#64748b)", background: "var(--card-2,#f6f8fb)", padding: "8px 10px", borderBottom: "1px solid var(--line,#e5e7eb)", whiteSpace: "nowrap" },
  td: { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--line,#e5e7eb)", whiteSpace: "nowrap" },
  tdR: { textAlign: "right", padding: "8px 10px", borderBottom: "1px solid var(--line,#e5e7eb)", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  chip: { fontSize: 10.5, fontWeight: 700, padding: "2px 7px", borderRadius: 6 },
  auditNote: { fontSize: 11, color: "var(--muted,#64748b)", lineHeight: 1.6 },
  flags: { display: "flex", flexWrap: "wrap", gap: 8 },
  flag: { display: "inline-flex", alignItems: "center", gap: 7, padding: "6px 11px", borderRadius: 8, fontSize: 12, fontWeight: 600 },
  flagDot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  flagN: { fontSize: 14, fontWeight: 800, fontVariantNumeric: "tabular-nums" },
  flagL: { opacity: 0.9 },
  break: { display: "flex", flexDirection: "column", gap: 6, border: "1px solid var(--line,#e5e7eb)", borderRadius: 10, padding: "12px 14px", background: "var(--card-2,#f6f8fb)" },
  brow: { display: "grid", gridTemplateColumns: "88px 1fr 66px", alignItems: "center", gap: 8, fontSize: 12 },
  track: { height: 10, borderRadius: 4, background: "var(--line,#e5e7eb)", overflow: "hidden" },
  rnum: { textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 },
  twoCol: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 14, alignItems: "start" },
};
